import * as React from 'react';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import {
  type AIMessageActionKey,
  ALL_AI_MESSAGE_ACTION_KEYS,
} from 'bundles/enterprise-admin-messaging-v2/utils/aiMessageActions';
import { sliceWithWrap } from 'bundles/enterprise-admin-messaging-v2/utils/utils';

const STORAGE_KEY = 'enterprise-admin-messaging-v2.define-goal-suggestion-offset';
const SUGGESTION_COUNT = 4;

type Result = {
  /** Suggestion keys to render as chips on the Define-goal screen. */
  suggestions: AIMessageActionKey[];
  /**
   * Advance the rotation by `SUGGESTION_COUNT` and persist for the NEXT
   * modal open. Does NOT mutate the currently-rendered chips — the open
   * session stays visually stable until the screen unmounts.
   */
  refresh: () => void;
};

const deserializeOffset = (raw: string): number => {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const readOffset = (): number => localStorageEx.getItem(STORAGE_KEY, deserializeOffset, 0);

const writeOffset = (offset: number) => {
  localStorageEx.setItem(STORAGE_KEY, offset, String);
};

/**
 * Rotates the suggestion chips on the Define-goal screen through the
 * registry in a fixed, deterministic order.
 *
 * Behaviour:
 *   - Every admin who opens the modal for the first time sees the first
 *     `SUGGESTION_COUNT` prompts in registry order.
 *   - The same set is shown for every render/open until the admin clicks
 *     Generate.
 *   - On Generate, the stored offset advances by `SUGGESTION_COUNT` and
 *     wraps modulo the registry length. The currently-open modal is NOT
 *     re-rendered — the new set surfaces only on the next mount.
 */
const useDefineGoalSuggestions = (): Result => {
  // Lazy initial state: read the persisted offset exactly once on mount and
  // freeze the visible suggestion set for this session.
  const [suggestions] = React.useState<AIMessageActionKey[]>(() => sliceWithWrap(readOffset(), SUGGESTION_COUNT));

  const refresh = React.useCallback(() => {
    const total = ALL_AI_MESSAGE_ACTION_KEYS.length;
    if (total === 0) return;
    const next = (readOffset() + SUGGESTION_COUNT) % total;
    writeOffset(next);
  }, []);

  return { suggestions, refresh };
};

export default useDefineGoalSuggestions;
export { STORAGE_KEY as DEFINE_GOAL_SUGGESTION_OFFSET_STORAGE_KEY, SUGGESTION_COUNT };
